import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CalendarBlank, User, Ruler, CheckCircle } from "@phosphor-icons/react";
import { JOB_STAGES, STAGE_DOT } from "@/components/StageBadge";
import { StageAge, isStalled } from "@/components/StageAge";
import { MarginChip, isUnprofitable } from "@/components/MarginChip";
import { templatorDiff } from "@/lib/templatorDiff";

const nextDate = (j) => j.template_date || j.fab_date || j.install_date;

export const JobStageBoard = ({ jobs, stageFilter, onlyStalled, onlyLosing = false, onMove }) => {
  const [dragId, setDragId] = useState(null);
  const [overStage, setOverStage] = useState(null);
  const navigate = useNavigate();

  return (
    <div className="grid gap-4 lg:grid-cols-6" data-testid="job-board">
      {JOB_STAGES.filter(
        (stage) => stageFilter === "All" || stage === stageFilter,
      ).map((stage) => {
        const groupKey = (j) =>
          `${j.customer_id || j.customer_name || ""}|${(j.address || j.project_name || "")
            .toLowerCase()
            .trim()}`;
        const filtered = jobs.filter(
          (j) =>
            j.stage === stage &&
            (!onlyStalled || isStalled(j)) &&
            (!onlyLosing || isUnprofitable(j)),
        );
        // Keep same-address jobs adjacent (first-appearance order) so separate
        // jobs for one address read as a group instead of looking duplicated.
        const buckets = {};
        const order = [];
        for (const j of filtered) {
          const k = groupKey(j);
          if (!buckets[k]) {
            buckets[k] = [];
            order.push(k);
          }
          buckets[k].push(j);
        }
        const column = order.flatMap((k) => buckets[k]);
        const groupCounts = Object.fromEntries(order.map((k) => [k, buckets[k].length]));
        return (
          <div
            key={stage}
            data-testid={`stage-column-${stage}`}
            onDragOver={(e) => {
              e.preventDefault();
              setOverStage(stage);
            }}
            onDragLeave={() => setOverStage(null)}
            onDrop={() => {
              if (dragId) onMove(dragId, stage);
              setDragId(null);
              setOverStage(null);
            }}
            className={`rounded-lg border p-3 transition-colors duration-150 ${
              overStage === stage
                ? "border-indigo-500 bg-indigo-500/[0.04]"
                : "border-zinc-800"
            }`}
          >
            <div className="flex items-center gap-2 pb-3">
              <span className={`h-2 w-2 rounded-full ${STAGE_DOT[stage]}`} />
              <span className="text-xs font-semibold uppercase tracking-widest text-zinc-400">
                {stage}
              </span>
              <span className="font-mono-data ml-auto text-xs text-zinc-500">
                {column.length}
              </span>
            </div>

            <div className="space-y-2">
              {column.length === 0 && (
                <p className="rounded-md border border-dashed border-zinc-800 p-4 text-center text-xs text-zinc-600">
                  Empty
                </p>
              )}
              {column.map((j, i) => {
                const tdiff = templatorDiff(j.templator_template);
                const contd = i > 0 && groupKey(column[i - 1]) === groupKey(j);
                const groupSize = groupCounts[groupKey(j)] || 1;
                return (
                <div
                  key={j.id}
                  draggable
                  onDragStart={() => setDragId(j.id)}
                  onDragEnd={() => setDragId(null)}
                  data-testid={`job-card-${j.id}`}
                  className={`group flex cursor-grab overflow-hidden rounded-lg border bg-zinc-900 transition-all duration-150 hover:-translate-y-0.5 active:cursor-grabbing ${
                    contd ? "ml-5" : ""
                  } ${
                    isStalled(j)
                      ? "border-red-500/50 hover:border-red-400"
                      : "border-zinc-800 hover:border-zinc-700"
                  }`}
                >
                  <span
                    className={`w-1 shrink-0 ${
                      isStalled(j)
                        ? "bg-red-400"
                        : STAGE_DOT[j.stage] || "bg-zinc-600"
                    }`}
                  />
                  <div className="min-w-0 flex-1 p-3">
                    {contd && (
                      <div
                        data-testid={`job-card-sameaddr-${j.id}`}
                        className="mb-1.5 flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-indigo-300/80"
                      >
                        ↳ Same address
                      </div>
                    )}
                    <Link to={`/projects/${j.id}?tab=field`} className="block">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5">
                          <span className="font-mono-data text-[10px] uppercase tracking-widest text-yellow-500">
                            {j.code}
                          </span>
                          {j.quote_id && (
                            <span
                              role="link"
                              tabIndex={0}
                              data-testid={`job-card-quote-chip-${j.id}`}
                              title="Open quote pricing"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                navigate(`/quotes/${j.quote_id}?step=3`);
                              }}
                              className="font-mono-data inline-flex cursor-pointer items-center rounded-full border border-indigo-500/40 bg-indigo-500/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-indigo-300 transition-colors duration-150 hover:bg-indigo-500/20"
                            >
                              {j.quote_code || "Quote"}
                            </span>
                          )}
                          {j.awaiting_deposit && (
                            <span
                              data-testid={`job-deposit-unpaid-${j.id}`}
                              className="rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-400"
                            >
                              Deposit
                            </span>
                          )}
                          {tdiff?.changed && (
                            <span
                              data-testid={`job-template-diff-${j.id}`}
                              title="Field template area differs from the quoted area — review before scheduling"
                              className="inline-flex items-center gap-0.5 rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-400"
                            >
                              <Ruler size={9} weight="bold" />
                              {tdiff.delta > 0 ? "+" : ""}
                              {tdiff.delta} ft²
                            </span>
                          )}
                          {j.appt_confirmed_at && (
                            <span
                              data-testid={`job-card-confirmed-${j.id}`}
                              title="Customer confirmed by text"
                              className="inline-flex items-center gap-0.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-300"
                            >
                              <CheckCircle size={9} weight="fill" /> Confirmed
                            </span>
                          )}
                        </div>
                        <StageAge job={j} testId={`job-age-${j.id}`} />
                      </div>
                      <div className="mt-1 flex items-center gap-1.5">
                        <span className="truncate text-sm font-bold leading-snug">
                          {j.project_name}
                        </span>
                        {groupSize > 1 && !contd && (
                          <span
                            data-testid={`job-group-count-${j.id}`}
                            title={`${groupSize} jobs at this address`}
                            className="shrink-0 rounded-full bg-indigo-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-indigo-300"
                          >
                            ×{groupSize}
                          </span>
                        )}
                      </div>
                      <div className="truncate text-xs text-zinc-400">
                        {j.customer_name || "—"}
                      </div>
                      {j.deal_only && (
                        <span
                          data-testid={`job-card-deal-only-${j.id}`}
                          className="mt-1.5 inline-flex rounded-full border border-dashed border-zinc-600 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-zinc-400"
                        >
                          No field work yet
                        </span>
                      )}
                      {j.money && (j.money.invoiced > 0 || j.money.cost_total > 0) && (
                        <div className="mt-1.5">
                          <MarginChip job={j} testId={`job-card-margin-${j.id}`} compact />
                        </div>
                      )}
                      {(nextDate(j) ||
                        j.install_crew ||
                        j.fab_crew ||
                        j.template_crew) && (
                        <div className="mt-2.5 flex flex-wrap items-center gap-1.5 border-t border-zinc-800 pt-2.5">
                          {nextDate(j) && (
                            <span className="font-mono-data inline-flex items-center gap-1 rounded-full bg-zinc-950 px-2 py-0.5 text-[10px] text-zinc-300">
                              <CalendarBlank
                                size={11}
                                weight="bold"
                                className="text-zinc-500"
                              />
                              {nextDate(j)}
                            </span>
                          )}
                          {(j.install_crew || j.fab_crew || j.template_crew) && (
                            <span className="inline-flex min-w-0 items-center gap-1 rounded-full bg-zinc-950 px-2 py-0.5 text-[10px] text-zinc-300">
                              <User
                                size={11}
                                weight="bold"
                                className="shrink-0 text-zinc-500"
                              />
                              <span className="truncate">
                                {j.install_crew || j.fab_crew || j.template_crew}
                              </span>
                            </span>
                          )}
                        </div>
                      )}
                    </Link>
                    <select
                      data-testid={`job-stage-select-${j.id}`}
                      value={j.stage}
                      onChange={(e) => onMove(j.id, e.target.value)}
                      className="mt-2 w-full rounded border border-zinc-700 bg-zinc-950 px-2 py-1 text-[11px] text-zinc-300 lg:hidden"
                    >
                      {JOB_STAGES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};
